/* app/providers/ConfirmProvider.jsx — единый confirm-диалог (// заменяет: window.confirm).
   API: const confirm = useConfirm(); await confirm({title, body, confirmLabel,
        cancelLabel, tone}) → boolean.
   F41: опция checkboxLabel (+checkboxDefault) — чекбокс в теле диалога; при
        подтверждении промис резолвится {ok:true, checked} (truthy — старые
        вызовы `if (!ok)` работают без изменений), при отмене — false.
   Автофокус — на БЕЗОПАСНОЙ кнопке (Отмена), даже для danger (баг авто-сабмита).
   Рендерится через Modal-хром, z-confirm (> modal).
   Доступность (kitchen-ux K-09, C-50): alertdialog назван заголовком (Modal связывает его
   сам) и описан текстом тела (describedBy); флажок — Checkbox из UI-kit (цель касания
   44 px и видимый фокус), а не «сырой» input 13×13 со встроенными стилями.
   Вопрос держит вкладку в её пространстве (lib/dialogHold.js, kitchen-ux A2 круг 4, S10): от
   вызова confirm() — синхронно, до отрисовки — и до ответа. Между чтением и подтверждённой
   записью вкладку не переключит соседняя, иначе запись ушла бы с чужим X-Workspace-Id. */

import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Checkbox } from '../../components/ui/Field.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { holdDialog } from '../../lib/dialogHold.js';

  const { createContext, useContext, useState, useCallback, useRef, useEffect, useId } = React;

  const ConfirmContext = createContext(null);

  function ConfirmProvider({ children }) {
    const [req, setReq] = useState(null);
    const [checked, setChecked] = useState(false);
    const resolver = useRef(null);
    const bodyId = useId();
    const release = useRef(null);
    const letGo = () => { if (release.current) { release.current(); release.current = null; } };
    useEffect(() => letGo, []);

    // Разрушительность подтверждения можно объявить двумя способами: tone:'danger'
    // (канон) и danger:true (интуитивный вариант, которым пользуется вся «Академия» —
    // 11 вызовов). Раньше читался только tone, поэтому «Удалить вместе с работами
    // учеников», «Удалить курс» и «Отчислить» рисовались обычной синей кнопкой:
    // самое опасное действие в разделе выглядело как согласие с уведомлением.
    const isDangerTone = (req) => !!req && (req.tone === 'danger' || req.danger === true);

    const confirm = useCallback((options) => {
      return new Promise((resolve) => {
        letGo();                                  // прежний вопрос (перезаписан) — не копим учёт
        release.current = holdDialog();
        resolver.current = resolve;
        setChecked(!!(options && options.checkboxDefault));
        setReq({
          title: 'Подтвердите действие',
          body: '',
          confirmLabel: 'Подтвердить',
          cancelLabel: 'Отмена',
          tone: 'accent', // 'danger' для разрушительных
          checkboxLabel: null,
          checkboxDefault: false,
          ...options,
        });
      });
    }, []);

    const close = useCallback((result) => {
      if (resolver.current) { resolver.current(result); resolver.current = null; }
      letGo();
      setReq(null);
    }, []);

    // подтверждение: с чекбоксом — {ok, checked} (truthy), без — true как раньше.
    const acceptRef = useRef(null);
    acceptRef.current = () => {
      if (req && req.checkboxLabel) close({ ok: true, checked });
      else close(true);
    };

    const value = { confirm };

    // Хром — единый <Modal> (role=alertdialog, z-confirm поверх обычных модалок).
    // Автофокус — на БЕЗОПАСНОЙ кнопке (Отмена) даже для danger (баг авто-сабмита).
    return (
      <ConfirmContext.Provider value={value}>
        {children}
        <Modal open={!!req} onClose={() => close(false)} size="sm" role="alertdialog"
          zIndex="var(--z-confirm)" className="modal--confirm" autoFocusSelector="[data-safe]"
          title={req ? req.title : undefined}
          describedBy={req && req.body ? bodyId : undefined}
          footer={req ? <>
            <Button variant="ghost" data-safe onClick={() => close(false)}>{req.cancelLabel}</Button>
            <Button variant={isDangerTone(req) ? 'danger' : 'primary'} onClick={() => acceptRef.current()}>{req.confirmLabel}</Button>
          </> : undefined}>
          {req && req.body ? <div className="t-body text-dim" id={bodyId}>{req.body}</div> : null}
          {req && req.checkboxLabel ? (
            <Checkbox className="confirm-dialog__check" label={req.checkboxLabel} checked={checked}
              onChange={(e) => setChecked(e.target.checked)} />
          ) : null}
        </Modal>
      </ConfirmContext.Provider>
    );
  }

  const useConfirm = () => useContext(ConfirmContext).confirm;

export { ConfirmProvider };
export { useConfirm };
